import React, { useState, forwardRef } from 'react';
import { BlockButton } from './BlockButton';
import { MarkButton } from './MarkButton';
import { CustomMarkButton } from './CustomMarkButton';
import Tippy from '@tippyjs/react';
import { LanguageSelector } from 'app/components';
import { Modal, Dropdown } from 'app/components';
import { ImportImage, ImportTable, ImportReference, ImportToC } from 'app/components/editor/import-area';
import { i18n } from 'app/utils/i18n';
import { useHistory } from 'react-router-dom';
import { MarkHeadingDropdown } from './MarkHeadingDropdown';
import { useEditorSelector } from 'app/state/contexts/EditorContext';
import { LinkButton, LinkContent } from './LinkButton';
import { BookmarkButton, BookmarkContent } from './BookmarkButton';
import { ColorButton, ColorPicker } from './ColorButton';
import { EditorModePicker } from 'app/components/editor/subtoolbar/tags/EditorModePicker';
import { useEditorMode } from 'app/state/contexts/EditorContext';

const LazyTippy = forwardRef(({ children, content: ContentComponent, ...props }, ref) => {
  const [mounted, setMounted] = useState(false);

  return (
    <Tippy {...props} ref={ref} content={mounted ? <ContentComponent /> : null} onMount={() => setMounted(true)} onHidden={() => setMounted(false)}>
      {children}
    </Tippy>
  );
});

const StylingComponent = () => {
  const { mode, setMode } = useEditorMode();

  return (
    <>
      <EditorModePicker currentMode={mode} onModeChange={setMode} />
      <div className="d-flex align-items-center">
        <MarkHeadingDropdown />
      </div>
      <div className="divider" />
      <Tippy content={<p className="gaby-tooltip arrow-top">{i18n('sub-toolbar.tooltip.bold')}</p>} placement="bottom">
        <MarkButton format="bold" icon="bold" text="Bold" css="toolbar-btn" />
      </Tippy>
      <Tippy content={<p className="gaby-tooltip arrow-top">{i18n('sub-toolbar.tooltip.italic')}</p>} placement="bottom" theme="light">
        <MarkButton format="italic" icon="italic" text="Italic" css="toolbar-btn" />
      </Tippy>
      <Tippy content={<p className="gaby-tooltip arrow-top">{i18n('sub-toolbar.tooltip.underline')}</p>} placement="bottom" theme="light">
        <MarkButton format="underline" icon="underline" text="Underlined" css="toolbar-btn" />
      </Tippy>
      <div className="divider" />
      <Tippy content={<p className="gaby-tooltip arrow-top">{i18n('sub-toolbar.tooltip.align-left')}</p>} placement="bottom" theme="light">
        <BlockButton format="custom-formatting" icon="align-left" styling={{ alignment: 'align-left' }} text="Align left" css="toolbar-btn" />
      </Tippy>
      <Tippy content={<p className="gaby-tooltip arrow-top">{i18n('sub-toolbar.tooltip.align-center')}</p>} placement="bottom" theme="light">
        <BlockButton format="custom-formatting" styling={{ alignment: 'align-center' }} icon="align-center" text="Align center" css="toolbar-btn" />
      </Tippy>
      <Tippy content={<p className="gaby-tooltip arrow-top">{i18n('sub-toolbar.tooltip.align-justify')}</p>} placement="bottom" theme="light">
        <BlockButton format="custom-formatting" styling={{ alignment: 'align-justify' }} icon="align-justify" text="Align justify" css="toolbar-btn" />
      </Tippy>
      <Tippy content={<p className="gaby-tooltip arrow-top">{i18n('sub-toolbar.tooltip.align-right')}</p>} placement="bottom" theme="light">
        <BlockButton format="custom-formatting" styling={{ alignment: 'align-right' }} icon="align-right" text="Align right" css="toolbar-btn" />
      </Tippy>
      <div className="divider" />
      <Tippy content={<p className="gaby-tooltip arrow-top">{i18n('sub-toolbar.tooltip.list-ol')}</p>} placement="bottom" theme="light">
        <BlockButton format="numbered-list" icon="list-ol" text="Ordered list" css="toolbar-btn" />
      </Tippy>
      <Tippy content={<p className="gaby-tooltip arrow-top">{i18n('sub-toolbar.tooltip.list-ul')}</p>} placement="bottom" theme="light">
        <BlockButton format="bulleted-list" icon="list-ul" text="Unordered list" css="toolbar-btn" />
      </Tippy>
      <div className="divider" />
      <Tippy content={<p className="gaby-tooltip arrow-top">{i18n('sub-toolbar.tooltip.indent')}</p>} placement="bottom" theme="light">
        <BlockButton format="custom-formatting" styling={{ indent: true }} icon="indent" text="indent" css="toolbar-btn" />
      </Tippy>
      <Tippy content={<p className="gaby-tooltip arrow-top">{i18n('sub-toolbar.tooltip.blockquote')}</p>} placement="bottom" theme="light">
        <BlockButton format="custom-formatting" styling={{ format: 'blockquote' }} icon="quote-left" text="Block quote" css="toolbar-btn" />
      </Tippy>
      <div className="divider" />
      <Tippy content={<p className="gaby-tooltip arrow-top">{i18n('sub-toolbar.tooltip.superscript')}</p>} placement="bottom" theme="light">
        <MarkButton format="superscript" icon="superscript" text="Superscript" css="toolbar-btn" />
      </Tippy>
      <Tippy content={<p className="gaby-tooltip arrow-top">{i18n('sub-toolbar.tooltip.subscript')}</p>} placement="bottom" theme="light">
        <MarkButton format="subscript" icon="subscript" text="Subscript" css="toolbar-btn" />
      </Tippy>
      <div className="divider" />
      <LazyTippy content={BookmarkContent} placement="bottom" theme="light" interactive={true} trigger="mouseenter click" appendTo="parent">
        <BookmarkButton format="bookmark" icon="bookmark" text="Bookmark" css="toolbar-btn" />
      </LazyTippy>
      <LazyTippy content={LinkContent} placement="bottom" theme="light" interactive={true} trigger="mouseenter click" appendTo="parent">
        <LinkButton format="link" icon="link" text="Link" css="toolbar-btn" />
      </LazyTippy>
      <div className="divider" />
      <Tippy content={<ColorPicker type="backgroundColor" />} placement="bottom" theme="light" interactive={true} trigger="mouseenter click" appendTo="parent">
        <ColorButton format="text" icon="highlighter" text="Background Color" css="toolbar-btn" />
      </Tippy>
      <Tippy content={<ColorPicker type="color" />} placement="bottom" theme="light" interactive={true} trigger="mouseenter click" appendTo="parent">
        <ColorButton format="text" icon="a" text="Text Color" css="toolbar-btn" />
      </Tippy>
    </>
  );
};

const InsertComponent = ({ setSelected, selected }) => {
  const items = [
    { title: i18n('toolbar.dropdown.insert-image'), icon: <i className="fa-regular fa-image" />, value: 'select_image' },
    { title: i18n('toolbar.dropdown.insert-list'), icon: <i className="fa-regular fa-table" />, value: 'select_table' },
    { title: i18n('toolbar.dropdown.insert-reference'), icon: <i className="fa-regular fa-link" />, value: 'select_reference' },
    { title: i18n('toolbar.dropdown.insert-toc'), icon: <i className="fa-regular fa-list-ol" />, value: 'select_toc' },
  ];
  const reduxActiveNode = useEditorSelector((editor) => editor.activeNode);

  return (
    <>
      <div className="divider" />
      <div className="d-flex align-items-center">
        <Dropdown sm onChange={setSelected} items={items} value={selected}>
          <Dropdown.DropdownButton
            title={i18n('sub-toolbar.dropdown.insert.title')}
            disabled={Object.keys(reduxActiveNode).length === 0}
            sm
            icon={
              <div
                className="bg-blue-300 text-blue-700 rounded-circle d-flex align-items-center justify-content-center"
                style={{ width: '1.25rem', height: '1.25rem' }}
              >
                <i className="fa-regular fa-plus" />
              </div>
            }
          />
        </Dropdown>
      </div>
    </>
  );
};

const LanguangeComponent = () => {
  const history = useHistory();
  const isDirty = useEditorSelector((editor) => editor.isDirty);
  const languages = useEditorSelector((editor) => editor.languages);
  const locale = useEditorSelector((editor) => editor.locale);
  const onChange = async (selectedLocale) => {
    const translation = languages.find((lang) => lang.locale === selectedLocale);
    if (translation && !translation.isDefault) {
      history.push(`?translation=${translation.superId}`);
    } else {
      history.push(`?`);
    }
    window.location.reload();
  };
  return (
    <div className="d-flex align-items-center">
      <LanguageSelector sm onChange={onChange} locale={locale} disabled={isDirty} />
    </div>
  );
};

const ShowFormattingComponent = ({ showFormatting, setShowFormatting }) => {
  return (
    <>
      <div className="divider" />
      <Tippy content={<p className="gaby-tooltip arrow-top">{i18n('sub-toolbar.tooltip.show-formatting')}</p>} placement="bottom">
        <CustomMarkButton icon="paragraph" text="Show Formatting" css="toolbar-btn" selected={showFormatting} setSelected={setShowFormatting} />
      </Tippy>
    </>
  );
};

export const SubToolbar = ({ enabled, showFormatting, setShowFormatting }) => {
  const [currentModal, setCurrentModal] = useState();
  return (
    <div className="sub-toolbar space-x-3">
      {!!enabled.styling ? <StylingComponent /> : null}
      <ShowFormattingComponent showFormatting={showFormatting} setShowFormatting={setShowFormatting} />
      {!!enabled.insert ? (
        <InsertComponent
          setSelected={(e) => {
            setCurrentModal(e);
          }}
          selected={currentModal}
        />
      ) : null}
      {!!enabled.languange ? <LanguangeComponent /> : null}
      <Modal show={currentModal === 'select_image'} onClose={setCurrentModal} size={'lg'} titleLabelKey={'imageimport.select-image'}>
        <ImportImage onClose={setCurrentModal} />
      </Modal>
      <Modal show={currentModal === 'select_table'} onClose={setCurrentModal}>
        <ImportTable onClose={setCurrentModal} />
      </Modal>
      <Modal show={currentModal === 'select_reference'} onClose={setCurrentModal}>
        <ImportReference onClose={setCurrentModal} />
      </Modal>
      <Modal titleLabelKey="toc.insert.modal-title" size="lg" show={currentModal === 'select_toc'} onClose={setCurrentModal}>
        <ImportToC onClose={setCurrentModal} />
      </Modal>
    </div>
  );
};
